import {
  css, type Interpolation,
} from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  type FC, type MouseEvent, type ReactNode,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import {
  TooltipBehavior, TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { type InputSize } from '../inputs';
import {
  DropDownItemSize, getRegularDropdownItemSize,
} from './regularDropdown.helpers';

const dropdownItemStyle = ({ height, disabled, highlight }: Readonly<{
  height: number;
  disabled?: boolean;
  highlight?: boolean;
}>) => (theme: Theme) => css({
  display: 'flex',
  fontSize: 16,
  lineHeight: '20px',
  padding: '0 8px',
  height,
  textAlign: 'left',
  color: disabled ? theme.textColors.disabled : highlight ? theme.textColors.warning : theme.textColors.primary,
  alignItems: 'center',
  alignContent: 'center',
  width: '100%',
  whiteSpace: 'nowrap',
  background: 'none',
  border: `1px solid ${theme.borderColors.primary}`,
  borderWidth: '1px 0 0',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxSizing: 'border-box',
  cursor: 'default',

  '&:hover': {
    backgroundColor: disabled ? 'inherit' : theme.dropdownColors.optionBackgroundHover,
  },
});

const ellipsisStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const iconStyle = css({
  fontSize: 16,
  marginRight: 3,
});

export type DropdownItemComponentProps = {
  className?: string;
  highlight?: boolean;
  icon?: IconProp;
  isDisabled?: boolean;
  itemIconStyle?: Interpolation;
  name: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  size: InputSize;
  subSize?: DropDownItemSize;
  isSelected?: boolean;
  tooltipContent?: ReactNode;
  tooltipPlacement?: TooltipPlacement;

  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
};

export const DropdownItemComponent: FC<DropdownItemComponentProps> = ({
  className,
  highlight,
  icon,
  isDisabled,
  itemIconStyle,
  name,
  onClick,
  prefix,
  suffix,
  size,
  subSize = DropDownItemSize.Default,
  isSelected,
  tooltipContent,
  tooltipPlacement = TooltipPlacement.Bottom,
  ...restProps
}) => {
  const itemHeight = getRegularDropdownItemSize(size, subSize);

  return (
    <TooltipComponent
      tooltipContent={tooltipContent}
      placement={tooltipPlacement}
      behavior={TooltipBehavior.ShowOnHover}
    >
      <Inline
        gap={8}
        wrap="nowrap"
        role="option"
        aria-selected={isSelected ?? false}
        tabIndex={-1}
        {...restProps}
        className={className}
        css={dropdownItemStyle({
          height: itemHeight,
          disabled: isDisabled,
          highlight,
        })}
        onClick={onClick}
      >
        {icon && (
          <FontAwesomeIcon
            icon={icon}
            css={[iconStyle, itemIconStyle]}
          />
        )}
        {prefix}
        {name && (
          <span css={ellipsisStyle}>
            {name}
          </span>
        )}
        {suffix}
      </Inline>
    </TooltipComponent>
  );
};
